import React, {useEffect, useMemo, useState} from 'react';
import {Card, Button, Text, Checkbox, Label, Icon} from '@gravity-ui/uikit';


import {useAddAppealForm} from '../../lib';
import {
  FormInput,
  TextEditor,
  useErrorHandler,
  ErrorModal,
  formatInputNumber,
  parseFormattedNumber,
} from '../../../../shared';
import styles from './AddAppealForm.module.scss';
import {useNavigate} from 'react-router';
import {DatePicker} from '@gravity-ui/date-components';
import {dateTimeParse} from '@gravity-ui/date-utils';
import {DateTime} from '@gravity-ui/date-utils';
import {Flame, Pencil, Plus} from '@gravity-ui/icons';
import {SelectRegions, SelectFunds, useCategoryGroupsWithCategories} from '../../../../entities';
import {SelectCategoriesByGroupsModal} from '../../../categories';

const FORMAT = 'DD.MM.YYYY';

export const AddAppealForm = () => {
  const navigate = useNavigate();
  const {handleError, isErrorModalOpen, errorInfo, handleClose} = useErrorHandler({
    resourceName: 'Сбор',
    title: 'Ошибка при создании сбора',
  });

  const {
    values,
    isValid,
    handleChange,
    setFieldValue,
    handleSubmit,
    handleBlur,
    touched,
    errors,
    isLoading,
    submitError,
  } = useAddAppealForm();

  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);
  const {data: categoryGroupsWithCategories} = useCategoryGroupsWithCategories();

  const categoryNameById = useMemo(() => {
    const map: Record<string, string> = {};
    (categoryGroupsWithCategories || []).forEach((group) => {
      group.categories.forEach((category) => {
        map[category.id] = category.name;
      });
    });
    return map;
  }, [categoryGroupsWithCategories]);

  const selectedCategoryNames = useMemo(
    () => values.general.categoryIds.map((id) => categoryNameById[id] || id),
    [values.general.categoryIds, categoryNameById],
  );

  const handleCategoriesApply = (ids: string[]) => {
    setFieldValue('general.categoryIds', ids);
  };

  const handleCategoryClose = (index: number) => {
    const newCategoryIds = [...values.general.categoryIds];
    newCategoryIds.splice(index, 1);
    setFieldValue('general.categoryIds', newCategoryIds);
  };

  // Обработчики изменения значений полей
  const handleFundChange = (newValue: string[]) => {
    setFieldValue('general.fundId', newValue[0] || '');
  };

  const handleRegionChange = (newValue: string[]) => {
    setFieldValue('general.regionId', newValue[0] || '');
  };

  const handleDateChange = (date: DateTime | null) => {
    setFieldValue('financialAndTiming.end', date);
  };

  const handleUrgentChange = (checked: boolean) => {
    setFieldValue('isUrgent', checked);
  };

  const handleEditorBlur = (content: string) => {
    const processedContent = content === '<p><br></p>' ? '' : content;
    setFieldValue('general.detailedDescription', processedContent);
  };

  // Специальный обработчик для поля суммы с форматированием
  const handleRequiredAmountChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const inputValue = event.target.value;

    // Разрешаем только цифры, пробелы и точку
    if (!/^[\d\s.]*$/.test(inputValue)) {
      return;
    }

    // Убираем форматирование и получаем чистое число
    const numericValue = parseFormattedNumber(inputValue);

    // Сохраняем числовое значение в форме
    setFieldValue('financialAndTiming.requiredAmount', numericValue);
  };

  // Специальный обработчик для поля комиссии
  const handleCommissionChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const inputValue = event.target.value;

    // Разрешаем только цифры и точку
    if (!/^[\d.]*$/.test(inputValue)) {
      return;
    }

    // Если поле пустое, устанавливаем 0
    const numericValue = inputValue === '' ? 0 : parseFloat(inputValue);

    // Сохраняем числовое значение в форме
    setFieldValue('financialAndTiming.commissionPercentage', numericValue);
  };

  // Получаем отформатированное значение для отображения
  const getFormattedRequiredAmount = () => {
    const amount = values.financialAndTiming.requiredAmount;
    return amount ? formatInputNumber(amount) : '';
  };

  // Получаем значение комиссии для отображения
  const getCommissionValue = () => {
    const commission = values.financialAndTiming.commissionPercentage;
    return commission !== null && commission !== undefined ? commission.toString() : '';
  };

  const handleCancel = () => {
    navigate('/fundraisings/appeals');
  };

  useEffect(() => {
    if (submitError) {
      handleError(submitError);
    }
  }, [submitError, handleError]);

  return (
    <>
      <form onSubmit={handleSubmit} className={styles.form}>
        <Card className={styles.content}>
          <Text variant="subheader-3">Сумма и дата сбора</Text>
          <FormInput
            label="Сумма (требуемая)"
            placeholder="Сумма (требуемая)"
            name="financialAndTiming.requiredAmount"
            value={getFormattedRequiredAmount()}
            onChange={handleRequiredAmountChange}
            size="s"
            error={touched.financialAndTiming?.requiredAmount && errors.financialAndTiming?.requiredAmount}
          />

          <FormInput
            label="Комиссия проекта"
            placeholder="Введите процент комиссии"
            name="financialAndTiming.commissionPercentage"
            value={getCommissionValue()}
            onChange={handleCommissionChange}
            size="s"
            isRequired={true}
            type="number"
            error={touched.financialAndTiming?.commissionPercentage && errors.financialAndTiming?.commissionPercentage}
          />

          <div className={styles.field}>
            <Text variant="subheader-1" className={styles.label}>
              Дата окончания сбора
            </Text>
            <DatePicker
              onUpdate={handleDateChange}
              size="s"
              value={values.financialAndTiming.end ? dateTimeParse(values.financialAndTiming.end) : null}
              placeholder="Выберите дату"
              format={FORMAT}
              hasClear
            />
            {touched.financialAndTiming?.end && errors.financialAndTiming?.end && (
              <Text variant="body-1" color="danger">
                {errors.financialAndTiming.end}
              </Text>
            )}
          </div>
          <div className={styles.field}>
            <div className={styles.checkboxLabel}>
              <Flame width={12} height={12} />
              <Text variant="subheader-1">Срочный сбор</Text>
            </div>

            <Checkbox
              className={styles.checkbox}
              checked={values.isUrgent}
              onChange={(e) => {
                handleUrgentChange(e.target.checked);
              }}
            />
          </div>
        </Card>

        <Card className={styles.content}>
          <Text variant="subheader-3">Информация о сборе</Text>

          <FormInput
            label="Название сбора"
            placeholder="Введите название сбора"
            name="general.name"
            value={values.general.name}
            onChange={handleChange}
            onBlur={handleBlur}
            size="m"
            isRequired={true}
            error={touched.general?.name && errors.general?.name}
          />

          <FormInput
            label="Короткое название"
            placeholder="Введите короткое название сбора"
            name="general.shortName"
            value={values.general.shortName || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            size="m"
            error={touched.general?.shortName && errors.general?.shortName}
          />
          <Text variant="body-1" color="secondary">
            Если не указано — в сообщениях используется полное название
          </Text>

          <FormInput
            label="Номер сбора"
            placeholder="Введите номер сбора (необязательно)"
            name="general.externalId"
            value={values.general.externalId || ''}
            onChange={(e) => {
              setFieldValue('general.externalId', e.target.value || null);
            }}
            onBlur={handleBlur}
            size="m"
            error={touched.general?.externalId && errors.general?.externalId}
          />

          <div className={styles.field}>
            <Text variant="subheader-1" className={styles.label}>
              Фонд<span className={styles.required}>*</span>
            </Text>
            <SelectFunds
              value={[values.general.fundId]}
              onUpdate={handleFundChange}
              name="fundId"
              error={touched.general?.fundId && errors.general?.fundId}
            />
          </div>

          <div className={styles.field}>
            <Text variant="subheader-1" className={styles.label}>
              Регион<span className={styles.required}>*</span>
            </Text>
            <SelectRegions
              value={[values.general.regionId]}
              onUpdate={handleRegionChange}
              name="regionId"
              error={touched.general?.regionId && errors.general?.regionId}
            />
          </div>
          <div className={styles.field}>
            <Text variant="subheader-1" className={styles.label}>
              Категории<span className={styles.required}>*</span>
            </Text>
            <div className={styles.categoriesPicker}>
              {selectedCategoryNames.length > 0 && (
                <div className={styles.categoryChips}>
                  {selectedCategoryNames.map((name, index) => (
                    <Label
                      type="close"
                      theme="normal"
                      onCloseClick={() => handleCategoryClose(index)}
                      key={`${values.general.categoryIds[index]}-${name}`}>
                      {name}
                    </Label>
                  ))}
                </div>
              )}
              <Button view="normal" size="m" onClick={() => setIsCategoriesModalOpen(true)}>
                <Icon data={values.general.categoryIds.length > 0 ? Pencil : Plus} />
                {values.general.categoryIds.length > 0 ? 'Редактировать' : 'Добавить категории'}
              </Button>

              {touched.general?.categoryIds && errors.general?.categoryIds && (
                <Text variant="body-1" color="danger">
                  {errors.general.categoryIds as string}
                </Text>
              )}
            </div>
          </div>

          <FormInput
            label="Чем поможем"
            placeholder="Краткое описание"
            name="general.shortDescription"
            value={values.general.shortDescription}
            onChange={handleChange}
            onBlur={handleBlur}
            size="m"
            isRequired={true}
            isTextarea
            error={touched.general?.shortDescription && errors.general?.shortDescription}
          />

          <div className={styles.textEditorContainer}>
            <Text variant="subheader-1" whiteSpace="nowrap">
              О сборе<span className={styles.required}>*</span>
            </Text>
            <div className={styles.editorContainer}>
              <TextEditor
                value={values.general.detailedDescription}
                onBlur={handleEditorBlur}
                name="general.detailedDescription"
                isError={!!errors.general?.detailedDescription}
              />
              {errors.general?.detailedDescription && (
                <Text variant="body-1" color="danger">
                  {errors.general.detailedDescription}
                </Text>
              )}
            </div>
          </div>
        </Card>

        <div className={styles.buttons}>
          <Button view="flat-secondary" size="l" onClick={handleCancel} disabled={isLoading} loading={isLoading}>
            Отменить
          </Button>
          <Button view="action" size="l" type="submit" disabled={!isValid} loading={isLoading}>
            Создать сбор
          </Button>
        </div>
      </form>
      <ErrorModal open={isErrorModalOpen} onClose={handleClose} title={errorInfo.title} message={errorInfo.message} />
      <SelectCategoriesByGroupsModal
        open={isCategoriesModalOpen}
        setOpen={setIsCategoriesModalOpen}
        selectedCategoryIds={values.general.categoryIds}
        onApply={handleCategoriesApply}
      />
    </>
  );
};
